/**
 * CommandPalette - Global navigation and action modal
 * 
 * Triggered by Cmd+K (Mac) or Ctrl+K (Windows/Linux)
 * Provides fuzzy search across pages, settings, and quick actions
 * Filters results based on user's Cognito groups (permissions)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { authService } from '../../services/auth-service';
import { 
  navigationItems, 
  filterNavigationByGroups, 
  searchNavigation,
  type NavigationItem,
} from '../Navigation/navigationConfig';
import type { PagePanelId } from '../../types/panel-types';
import { Button, Input } from '@/components/ui';
import { useAutocompleteKeyboard } from '../../hooks/useAutocompleteKeyboard';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (panelId: PagePanelId) => void;
  onNavigateToPath?: (path: string) => void; // For full-page navigation (e.g., /wizard)
  onLogout?: () => void;
  onOpenCalculator?: () => void;
  userGroups?: string[];
}

interface ActionItem {
  id: string;
  label: string;
  icon: string;
  action: () => void;
  keywords?: string[];
}

export function CommandPalette({
  isOpen,
  onClose,
  onNavigate,
  onNavigateToPath,
  onLogout,
  onOpenCalculator,
  userGroups: propUserGroups = [],
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [localUserGroups, setLocalUserGroups] = useState<string[]>(propUserGroups);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { setTheme } = useTheme();
  
  // Fetch user groups directly if not provided via props
  useEffect(() => {
    if (propUserGroups.length === 0) {
      authService.getCurrentUser().then(user => {
        if (user?.groups) {
          setLocalUserGroups(user.groups);
        }
      });
    } else {
      setLocalUserGroups(propUserGroups);
    }
  }, [propUserGroups]);
  
  // Filter navigation items based on user permissions
  const accessibleItems = filterNavigationByGroups(navigationItems, localUserGroups);
  
  // Search/filter items
  const filteredNavItems = searchNavigation(accessibleItems, query);
  
  // Quick actions - memoized to prevent dependency changes
  const actions = useMemo<ActionItem[]>(() => {
    const baseActions: ActionItem[] = [
      {
        id: 'theme-light',
        label: 'Switch to Light Theme',
        icon: '☀️',
        action: () => { setTheme('light'); onClose(); },
        keywords: ['theme', 'light', 'appearance'],
      },
      {
        id: 'theme-dark',
        label: 'Switch to Dark Theme',
        icon: '🌙',
        action: () => { setTheme('dark'); onClose(); },
        keywords: ['theme', 'dark', 'appearance'],
      },
      {
        id: 'theme-system',
        label: 'Use System Theme',
        icon: '🔄',
        action: () => { setTheme('auto'); onClose(); },
        keywords: ['theme', 'system', 'auto', 'appearance'],
      },
    ];
    
    // Add calculator action if callback provided
    if (onOpenCalculator) {
      baseActions.push({
        id: 'calculator',
        label: 'Open Engineering Calculator',
        icon: '⚡',
        action: () => { onOpenCalculator(); onClose(); },
        keywords: ['calculator', 'ohm', 'voltage', 'current', 'resistance', 'power', 'math', 'hex', 'binary', 'electrical'],
      });
    }
    
    if (onLogout) {
      baseActions.push({
        id: 'logout',
        label: 'Log Out',
        icon: '🚪',
        action: () => { onLogout(); onClose(); },
        keywords: ['logout', 'sign out', 'exit'],
      });
    }
    
    return baseActions;
  }, [setTheme, onClose, onLogout, onOpenCalculator]);
  
  // Filter actions by query - memoized to prevent dependency changes
  const filteredActions = useMemo(() => query.trim()
    ? actions.filter(a => 
        a.label.toLowerCase().includes(query.toLowerCase()) ||
        a.keywords?.some(k => k.toLowerCase().includes(query.toLowerCase()))
      )
    : [], [query, actions]);
  
  // Combined list for navigation - memoized to prevent dependency changes on every render
  const allItems = useMemo<Array<{ type: 'nav' | 'action'; item: NavigationItem | ActionItem }>>(() => [
    ...filteredNavItems.map(item => ({ type: 'nav' as const, item })),
    ...filteredActions.map(item => ({ type: 'action' as const, item })),
  ], [filteredNavItems, filteredActions]);
  
  // Group by category for display
  const pageItems = filteredNavItems.filter(i => i.category === 'pages');
  const settingsItems = filteredNavItems.filter(i => i.category === 'settings');
  const adminItems = filteredNavItems.filter(i => i.category === 'admin');
  
  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);
  
  // Handle selection (defined before hook that uses it)
  const handleSelect = useCallback((item: { type: 'nav' | 'action'; item: NavigationItem | ActionItem }) => {
    if (item.type === 'nav') {
      const navItem = item.item as NavigationItem;
      if (navItem.isFullPage && onNavigateToPath) {
        onNavigateToPath(navItem.path);
        onClose();
      } else {
        onNavigate(navItem.panelId);
        onClose();
      }
    } else {
      const actionItem = item.item as ActionItem;
      actionItem.action();
    }
  }, [onNavigate, onNavigateToPath, onClose]);

  const handleKeySelect = useCallback((index: number) => {
    if (allItems[index]) {
      handleSelect(allItems[index]);
    }
  }, [allItems, handleSelect]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: allItems.length,
    onSelect: handleKeySelect,
    onClose,
    enabled: isOpen,
    scrollIntoView: false,
    resetDeps: [query],
  });
  
  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  // Track index for rendering
  let currentIndex = 0;
  
  const renderItem = (item: NavigationItem | ActionItem, type: 'nav' | 'action', idx: number) => {
    const isSelected = selectedIndex === idx;
    const icon = type === 'nav' ? (item as NavigationItem).icon : (item as ActionItem).icon;
    const label = item.label;
    
    return (
      <Button
        key={type === 'nav' ? (item as NavigationItem).panelId : (item as ActionItem).id}
        data-index={idx}
        onClick={() => handleSelect({ type, item })}
        variant="ghost"
        className={`w-full text-left px-4 py-2.5 h-auto flex items-center gap-3 justify-start rounded-none ${
          isSelected
            ? 'bg-info-light text-info'
            : 'text-foreground hover:bg-muted'
        }`}
      >
        <span className="text-lg w-6 text-center">{icon}</span>
        <span className="flex-1">{label}</span>
        {isSelected && (
          <span className="text-xs text-muted-foreground">↵</span>
        )}
      </Button>
    );
  };
  
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Modal */}
      <div
        ref={containerRef}
        className="relative w-full max-w-lg bg-card rounded-xl shadow-2xl border border-border overflow-hidden"
      >
        {/* Search Input */}
        <div className="flex items-center px-4 py-3 border-b border-border">
          <span className="text-muted-foreground mr-3">🔍</span>
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, settings, or actions..."
            className="flex-1 bg-transparent border-none shadow-none text-base focus-visible:ring-0"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-muted border border-border rounded text-xs text-muted-foreground">
            esc
          </kbd>
        </div>
        
        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto">
          {allItems.length === 0 ? (
            <div className="px-4 py-8 text-center text-muted-foreground">
              <div className="text-3xl mb-2">🔍</div>
              <p>No results found for "{query}"</p>
            </div>
          ) : (
            <>
              {/* Pages */}
              {pageItems.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase bg-muted">
                    Pages
                  </div>
                  {pageItems.map(item => {
                    const idx = currentIndex++;
                    return renderItem(item, 'nav', idx);
                  })}
                </div>
              )}
              
              {/* Settings */}
              {settingsItems.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase bg-muted">
                    Settings
                  </div>
                  {settingsItems.map(item => {
                    const idx = currentIndex++;
                    return renderItem(item, 'nav', idx);
                  })}
                </div>
              )}
              
              {/* Admin */}
              {adminItems.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase bg-muted">
                    Admin
                  </div>
                  {adminItems.map(item => {
                    const idx = currentIndex++;
                    return renderItem(item, 'nav', idx);
                  })}
                </div>
              )}
              
              {/* Actions */}
              {filteredActions.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase bg-muted">
                    Actions
                  </div>
                  {filteredActions.map(item => {
                    const idx = currentIndex++;
                    return renderItem(item, 'action', idx);
                  })}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-2 border-t border-border bg-muted flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-card border border-border rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-card border border-border rounded">↓</kbd>
              <span className="ml-1">Navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-card border border-border rounded">↵</kbd>
              <span className="ml-1">Select</span>
            </span>
          </div>
          <span>{allItems.length} result{allItems.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CommandPalette;
